import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Automate, H3 } from '../components/docs/prose'
import { FlowMapDiagram } from '../components/docs/FlowMapDiagram'

export const Route = createFileRoute('/docs/flow-map')({ component: FlowMap })

const SECTIONS = [
  { id: 'nodes-edges', title: 'Nodes and edges' },
  { id: 'exact', title: 'Why the edges are exact' },
  { id: 'timeline', title: 'The timeline' },
  { id: 'reading', title: 'Reading the canvas' },
  { id: 'views', title: 'Saved views' },
]

function FlowMap() {
  return (
    <DocPage
      path="/docs/flow-map"
      title="Money flow map"
      lede={
        <>
          Your accounts as a graph, one month at a time, with the money
          animated along the edges. It is the answer to &ldquo;where did it all
          go&rdquo; in a form you can actually scan.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="nodes-edges" title="Nodes and edges">
        <P>
          Every account is a node — including the Income and Expense system
          accounts, which is what gives the graph a left edge and a right edge
          rather than a closed loop of your own accounts.
        </P>
        <FlowMapDiagram />
        <P>
          Nodes are laid out in four columns: income, then assets, then debts
          and investments, then expenses. Money reads left to right. Within a
          column, the busiest accounts sit highest.
        </P>
        <DefList
          items={[
            ['Node colour', 'Follows account type. Income and Asset use the inflow colour, Debt and Expense the outflow colour, Investment its own violet, Equity a neutral grey.'],
            ['Node size', 'Scales with the volume through it that month — inflow plus outflow. The area is proportional, not the radius, so a node twice as wide is four times the money.'],
            ['Edge width', 'Scales with the amount that moved between those two accounts, capped so one mortgage payment cannot flatten everything else.'],
            ['Edge colour', 'A gradient from the source account’s colour to the target’s, so a direction is readable at a glance.'],
          ]}
        />
      </Section>

      <Section id="exact" title="Why the edges are exact">
        <P>
          An edge exists between two accounts when a single journal entry
          touched both. That is the whole rule. There is no window in which a
          withdrawal and a deposit of similar size get guessed at as the same
          movement, which is how most flow visualisations are built and why
          they cannot be trusted at the dollar.
        </P>
        <H3>Split entries</H3>
        <P>
          Entries with more than two legs — a paycheque split across chequing,
          savings and an RRSP — are allocated proportionally. Each receiving
          account gets a share of each source in proportion to its size, so the
          totals on the edges add up to exactly the entry. Nothing is dropped
          and nothing is invented.
        </P>
        <P>
          Period totals count only the real accounts —{' '}
          <C>Asset</C>, <C>Investment</C> and <C>Debt</C>. Including the
          Income and Expense nodes would count every dollar twice, once at each
          end of its journey.
        </P>
      </Section>

      <Section id="timeline" title="The timeline">
        <P>
          The map is monthly. The slider along the bottom moves through the
          months in your ledger, and the play button walks it forward
          automatically.
        </P>
        <P>
          The scrub is continuous rather than snapped, and the frames between
          two months are interpolated — node sizes and edge widths blend, and
          edges that fade below a threshold drop out. That is a motion effect,
          not data. The label and any drill-down snap to the nearer real month,
          so nothing you can click on refers to a date that does not exist.
        </P>
      </Section>

      <Section id="reading" title="Reading the canvas">
        <P>
          Drag nodes to rearrange. Scroll to zoom, drag the background to pan.
          Hovering an account lifts it and everything it touches while dimming
          the rest, which is the fastest way to answer &ldquo;what does this
          account connect to&rdquo;.
        </P>
        <P>
          Clicking one opens a panel with its inflow, outflow and balance, the
          entries behind that month with their counterparties, and a colour
          picker if you want a particular account to stand out permanently.
        </P>
        <P>
          The particles are proportional too: busier edges carry more of them,
          moving faster. If you have reduced-motion turned on at the OS level
          they hold still rather than animating.
        </P>
      </Section>

      <Section id="views" title="Saved views">
        <P>
          A view is your arrangement of the graph, not a filter on the data. It
          stores where you dragged each node, which accounts you hid, any
          colours you overrode, the zoom and pan, and which month you were
          looking at.
        </P>
        <P>
          Names are unique per household, and one view can be the household
          default — enforced by a partial unique index rather than by
          application code, so two people saving at once cannot end up with two
          defaults. When a default exists it is applied on load, before the
          canvas computes a layout, which is what makes your arrangement stick
          instead of being overwritten by the automatic one.
        </P>
        <P>
          The stored layout mode is <C>column</C>. Force and Sankey layouts are
          reserved in the schema and not yet rendered.
        </P>
      </Section>

      <Automate>
        This map is only as good as the entries under it, and hand-entered
        transactions produce a sparse one. A connected account fills the graph
        in without you categorising anything: the counterparty leg comes from
        the transaction&rsquo;s own category, so the expense nodes appear on
        their own. See{' '}
        <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>Automating it</Link>.
      </Automate>
    </DocPage>
  )
}
